import { Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { SetFormActionWithId } from '../../actions/form-submission.action';
import { FormActionState } from '../../state/form-submision.state';
import { buildNestedPatch, getNestedValue } from '../../utils/nested-object-path.utils';

/**
 * Describes where a Form Widget's value belongs in FormActionState: `rootFormId` is the
 * FormActionState id to dispatch onto; `childKey` (with `arrayIndex` for array items) says how
 * deep under that root's formData the value nests. Undefined childKey means the value maps flat
 * onto the root (a 'root'-type Form Widget). Callers set this explicitly from formType/
 * parentFormID/childKey - it is never inferred from a path string's shape.
 */
export interface NestedFormDispatchMeta {
  rootFormId: string;
  childKey?: string;
  arrayIndex?: number;
}

/**
 * Dispatches a Form Widget's value onto FormActionState, nesting it under `meta.childKey`
 * (and `meta.arrayIndex` for array items) when present so it doesn't clobber sibling fields
 * already written under the same root - SetFormActionWithId's reducer only shallow-merges at
 * formData's top level. Shared by FormWidgetComponent (per-keystroke saves) and
 * FormButtonWidgetComponent (on-submit saves) so both follow the same nesting rules.
 */
export function dispatchNestedFormValue(
  store: Store,
  meta: NestedFormDispatchMeta,
  formValue: Record<string, unknown>,
): void {
  if (!meta.childKey) {
    store.dispatch(new SetFormActionWithId({ type: '', formData: formValue }, meta.rootFormId));
    return;
  }

  const currentFormData = store.selectSnapshot(FormActionState.getFormActionStateWithId(meta.rootFormId))
    ?.formData;

  if (meta.arrayIndex !== undefined) {
    const currentArray = getNestedValue<Record<string, unknown>[]>(currentFormData, meta.childKey) ?? [];
    const updatedArray = [...currentArray];
    updatedArray[meta.arrayIndex] = { ...updatedArray[meta.arrayIndex], ...formValue };
    store.dispatch(
      new SetFormActionWithId(
        { type: '', formData: buildNestedPatch(currentFormData, meta.childKey, updatedArray) },
        meta.rootFormId,
      ),
    );
    return;
  }

  const currentChild = getNestedValue<Record<string, unknown>>(currentFormData, meta.childKey);
  const mergedChild = { ...currentChild, ...formValue };
  store.dispatch(
    new SetFormActionWithId(
      { type: '', formData: buildNestedPatch(currentFormData, meta.childKey, mergedChild) },
      meta.rootFormId,
    ),
  );
}

export function checkUrlParamsForValuesAndStoreInFormState(formId: string, injector: Injector) {
  const activatedRoute = injector.get(ActivatedRoute);
  const store = injector.get(Store);
  const queryParams = activatedRoute?.snapshot?.queryParams;
  if (!queryParams || Object.keys(queryParams).length === 0) return;
  // Filter out unwanted keys
  const filteredParams: Record<string, string> = {};
  const excludedKeys = new Set(['direction', 'formSessionId', 'formId']);

  Object.entries(queryParams).forEach(([key, value]) => {
    if (!key.startsWith('utm_') && !excludedKeys.has(key)) {
      filteredParams[key] = value;
    }
  });

  if (Object.keys(filteredParams).length === 0) return;

  store.dispatch(
    new SetFormActionWithId(
      {
        type: formId,
        formData: {
          ...filteredParams,
        },
      },
      formId,
    ),
  );
}
